'use client'

import React from 'react'

interface HeroSectionProps {
  onScrollToModules: () => void
  onOpenAuth?: (tab?: 'login' | 'register') => void
}

// Faits PRODUIT vérifiables uniquement.
//
// Ce bandeau affichait quatre compteurs d'usage animés — « +2 400 notes
// techniques créées », « +1 800 transcriptions traitées », « +950
// codifications », « +620 rapports ». Aucun n'était mesuré : les valeurs
// étaient écrites en dur et l'animation leur donnait l'apparence d'un
// relevé. C'est une affirmation d'usage invérifiable sur un produit qui
// démarre, donc retirée.
//
// Ce qui les remplace est contrôlable dans le dépôt :
//   4      modules (transcription, codification, rapport, note technique)
//   98     langues — couverture du modèle whisper-1 utilisé
//   2 Go   MAX_FILE_SIZE_MB = 2048 (module-01.tsx), repris dans les CGU
//   3      formats d'export réellement proposés par des boutons :
//          Word (.docx), Excel (.xlsx), texte brut (.txt). Aucun PDF.
const STATS = [
  {
    value: '4',
    label: 'modules de recherche',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="3" y="2" width="12" height="16" rx="2" stroke="white" strokeWidth="1.5"/>
        <line x1="6" y1="7" x2="12" y2="7" stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
        <line x1="6" y1="10" x2="12" y2="10" stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
        <line x1="6" y1="13" x2="10" y2="13" stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    value: '98',
    label: 'langues transcrites',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="7" y="2" width="6" height="10" rx="3" stroke="white" strokeWidth="1.5"/>
        <path d="M4 9Q4 14 10 14Q16 14 16 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        <line x1="10" y1="14" x2="10" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="7" y1="17" x2="13" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    value: '2 Go',
    label: 'par fichier audio',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 10 L9 10 L13 6 L17 6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="5" cy="10" r="2" fill="white" opacity="0.7"/>
        <circle cx="11" cy="10" r="2" fill="white" opacity="0.7"/>
        <circle cx="15" cy="6" r="2.5" stroke="white" strokeWidth="1.3" fill="none"/>
      </svg>
    ),
  },
  {
    value: '3',
    label: "formats d'export",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="3" y="10" width="4" height="7" rx="1" fill="white" opacity="0.8"/>
        <rect x="8" y="6" width="4" height="11" rx="1" fill="white"/>
        <rect x="13" y="3" width="4" height="14" rx="1" fill="white" opacity="0.8"/>
      </svg>
    ),
  },
]

export default function HeroSection({ onScrollToModules, onOpenAuth }: HeroSectionProps) {
  // L'animation de comptage a été retirée avec les compteurs d'usage : faire
  // défiler un nombre de 0 à sa valeur suggère un relevé en train d'être lu.
  // Les faits produit qui les remplacent sont fixes, et s'affichent tels quels.

  return (
    <>
      {/* Self-contained hero styles */}
      <style>{`
        @keyframes hero-flow-dash  { to { stroke-dashoffset: -22; } }
        @keyframes hero-center-pulse {
          0%, 100% { r: 48px; opacity: 0.15; }
          50%       { r: 60px; opacity: 0; }
        }
        @keyframes hero-dot-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(1.3); }
        }
        .hero-flow-path {
          fill: none;
          stroke-width: 2;
          stroke-dasharray: 6 5;
          animation: hero-flow-dash 2s linear infinite;
        }
        .hero-flow-path.rev { animation-direction: reverse; }
        .hero-center-pulse {
          animation: hero-center-pulse 2.5s ease-in-out infinite;
          transform-origin: 210px 190px;
        }
        .hero-mod-circle { cursor: pointer; transition: filter 0.2s; }
        .hero-mod-circle:hover { filter: drop-shadow(0 6px 16px rgba(0,0,0,0.18)); }
        .hero-mod-tooltip { opacity: 0; pointer-events: none; transition: opacity 0.2s; }
        .hero-mod-circle:hover .hero-mod-tooltip { opacity: 1; }
        .hero-video-dot {
          display: inline-block;
          width: 7px; height: 7px;
          background: #e05a30;
          border-radius: 50%;
          flex-shrink: 0;
          animation: hero-dot-pulse 1.5s infinite;
        }
        @keyframes hero-pain-in {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes hero-arrow-bounce {
          0%, 100% { transform: translateX(0); }
          50%       { transform: translateX(4px); }
        }
        .hero-pain-item {
          display: flex; align-items: center; gap: 9px;
          padding: 4px 10px 4px 8px; border-radius: 7px;
          background: rgba(192,57,43,0.04);
          border: 1px solid rgba(192,57,43,0.10);
          opacity: 0; animation: hero-pain-in 0.4s ease forwards;
        }
        .hero-pain-cross {
          width: 16px; height: 16px; border-radius: 50%;
          background: #c0392b; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
        }
        .hero-pain-conclusion {
          display: flex; align-items: center; gap: 9px;
          margin-top: 2px; padding: 8px 13px;
          background: #2d5a27; border-radius: 9px; color: white;
          font-size: 13px; font-weight: 600;
          opacity: 0; animation: hero-pain-in 0.4s ease forwards;
        }
        .hero-pain-conclusion strong { color: white; font-weight: 800; }
        .hero-conclusion-arrow {
          font-size: 15px; font-weight: 800; flex-shrink: 0;
          animation: hero-arrow-bounce 1.5s ease-in-out infinite;
        }
        @media (max-width: 900px) {
          .hero-inner  { flex-direction: column !important; padding: 28px 24px !important; min-height: auto !important; gap: 28px !important; }
          .hero-right  { flex: none !important; width: 100% !important; }
          .hero-dgram  { width: 300px !important; height: auto !important; }
          .hero-sband  { padding: 20px 24px !important; flex-wrap: wrap !important; }
          .hero-sitem  { flex: 0 0 50% !important; border-right: none !important;
                         border-bottom: 1px solid rgba(255,255,255,0.12) !important;
                         padding: 14px 0 !important; margin: 0 !important; }
          .hero-sitem:nth-child(odd) { border-right: 1px solid rgba(255,255,255,0.12) !important; padding-right: 16px !important; }
          .hero-sitem:last-child,
          .hero-sitem:nth-last-child(2):nth-child(odd) { border-bottom: none !important; }
        }
      `}</style>

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section
        style={{
          background: '#f4f1ea',
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid #e2ddd4',
        }}
      >
        {/* Subtle green radial overlay (right side) */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse 60% 80% at 100% 50%, rgba(45,90,39,0.05) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          className="hero-inner"
          style={{
            maxWidth: 1140,
            margin: '0 auto',
            padding: '36px 60px',
            display: 'flex',
            alignItems: 'center',
            gap: 48,
            position: 'relative',
            minHeight: 480,
          }}
        >
          {/* ── LEFT ────────────────────────────────────────────────── */}
          <div style={{
            flex: 1,
            maxWidth: 520,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            fontFamily: 'var(--font-dm-sans), "DM Sans", sans-serif',
          }}>

            {/* Badge */}
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#e8f0e6',
              border: '1px solid rgba(45,90,39,0.2)',
              color: '#2d5a27',
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: 100,
              width: 'fit-content',
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
            }}>
              <span style={{ width: 6, height: 6, background: '#2d5a27', borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />
              Qualyse
            </span>

            {/* H1 */}
            <h1 className="qly-hero-h1" style={{
              fontFamily: 'var(--font-dm-serif), "DM Serif Display", serif',
              fontSize: 'clamp(34px, 3.5vw, 48px)',
              lineHeight: 1.1,
              letterSpacing: '-1px',
              color: '#1a1a1a',
              margin: 0,
            }}>
              Accélérez votre<br />
              <em style={{ fontStyle: 'italic', color: '#2d5a27' }}>étude qualitative</em>
            </h1>

            {/* Pain list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 440 }}>
              {([
                { delay: '0.10s', text: <>Plus de <strong style={{ color: '#1a1a1a' }}>nuits à transcrire</strong> ou corriger des fautes</> },
                { delay: '0.20s', text: <>Plus de <strong style={{ color: '#1a1a1a' }}>semaines perdues</strong> sur NVivo ou MAXQDA</> },
                { delay: '0.30s', text: <>Plus de <strong style={{ color: '#1a1a1a' }}>mois à rédiger</strong> une note technique</> },
                { delay: '0.40s', text: <>Plus de <strong style={{ color: '#1a1a1a' }}>mois à rédiger</strong> un rapport qualitatif</> },
              ] as { delay: string; text: React.ReactNode }[]).map((item, i) => (
                <div key={i} className="hero-pain-item" style={{ animationDelay: item.delay }}>
                  <div className="hero-pain-cross">
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <path d="M1 1l8 8M9 1L1 9" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <span style={{ fontSize: 13, color: '#3a3a32', lineHeight: 1.35 }}>{item.text}</span>
                </div>
              ))}
              <div className="hero-pain-conclusion" style={{ animationDelay: '0.52s' }}>
                <span className="hero-conclusion-arrow">→</span>
                <span><strong>Qualyse</strong> fait tout ça en quelques minutes.</span>
              </div>
            </div>

            {/* CTA row */}
            <div className="qly-hero-cta" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 8, marginTop: 4 }}>

              {/* Primary */}
              <button
                onClick={onScrollToModules}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#2d5a27', color: 'white',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  padding: '11px 16px', borderRadius: 8, border: 'none',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  transition: 'background 0.2s, transform 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#23451e'; e.currentTarget.style.transform = 'translateY(-1px)' }}
                onMouseLeave={e => { e.currentTarget.style.background = '#2d5a27'; e.currentTarget.style.transform = '' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7h10M8 3l4 4-4 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Commencer gratuitement
              </button>

              {/* Secondary — demo (animated red dot) */}
              <button
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'transparent', color: '#2d5a27',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  padding: '10px 14px', borderRadius: 8, border: '1.5px solid #2d5a27',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  transition: 'background 0.2s, transform 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#e8f0e6'; e.currentTarget.style.transform = 'translateY(-1px)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = '' }}
              >
                <span className="hero-video-dot" />
                Voir la démonstration
              </button>

              {/* Ghost */}
              <button
                onClick={() => onOpenAuth ? onOpenAuth('register') : (window.location.href = '/auth?redirect=/pricing')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'transparent', color: '#5a5a52',
                  fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
                  padding: '10px 13px', borderRadius: 8, border: '1.5px solid #d4cfc4',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  transition: 'border-color 0.2s, color 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2d5a27'; e.currentTarget.style.color = '#2d5a27' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#d4cfc4'; e.currentTarget.style.color = '#5a5a52' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M2 12c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                S&apos;inscrire / Se connecter
              </button>
            </div>

            {/* Trust row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 8 }}>
              {[
                { icon: '🔒', text: 'Données sécurisées' },
                { icon: '✅', text: 'Conforme RGPD' },
                { icon: '⚡', text: 'Résultats en minutes' },
              ].map(item => (
                <span key={item.text} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#5a5a52', fontWeight: 500 }}>
                  <span style={{ fontSize: 13 }}>{item.icon}</span>
                  {item.text}
                </span>
              ))}
            </div>
          </div>

          {/* ── RIGHT: SVG diagram ────────────────────────────────── */}
          <div
            className="hero-right"
            style={{ flex: '1 1 340px', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div
              className="hero-dgram"
              style={{ width: '100%', maxWidth: 360, aspectRatio: '360 / 340', position: 'relative' }}
            >
              <svg
                viewBox="0 0 420 380"
                aria-label="Schéma des 4 modules Qualyse"
                role="img"
                style={{ width: '100%', height: '100%', overflow: 'visible' }}
              >
                <defs>
                  <filter id="hglow" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="4" result="blur"/>
                    <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                  <filter id="hsh" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="rgba(0,0,0,0.12)"/>
                  </filter>
                  <linearGradient id="hgg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3d6b35"/>
                    <stop offset="100%" stopColor="#2d5a27"/>
                  </linearGradient>
                  <linearGradient id="hgt" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#2a9a8e"/>
                    <stop offset="100%" stopColor="#1e7a70"/>
                  </linearGradient>
                  <linearGradient id="hgo" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#9a7c3a"/>
                    <stop offset="100%" stopColor="#7a6230"/>
                  </linearGradient>
                  <linearGradient id="hgb" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#9a3030"/>
                    <stop offset="100%" stopColor="#7a3030"/>
                  </linearGradient>
                </defs>

                {/* Background circle */}
                <circle cx="210" cy="190" r="155" fill="white" opacity="0.5" filter="url(#hsh)"/>
                <circle cx="210" cy="190" r="155" fill="none" stroke="#e2ddd4" strokeWidth="1" strokeDasharray="4 4"/>

                {/* Center pulse ring */}
                <circle className="hero-center-pulse" cx="210" cy="190" r="48" fill="none" stroke="#2d5a27" strokeWidth="2"/>

                {/* Flow paths */}
                <path className="hero-flow-path"     d="M175 158 Q145 145 112 112" stroke="#3d6b35"/>
                <path className="hero-flow-path rev" d="M245 158 Q275 145 308 112" stroke="#1e7a70"/>
                <path className="hero-flow-path rev" d="M175 222 Q145 235 112 268" stroke="#7a6230"/>
                <path className="hero-flow-path"     d="M245 222 Q275 235 308 268" stroke="#7a3030"/>

                {/* ── Module: Notes Techniques (top-left, 90 90) ── */}
                <g className="hero-mod-circle" transform="translate(90,90)">
                  <circle r="52" cx="0" cy="0" fill="white" filter="url(#hsh)"/>
                  <circle r="52" cx="0" cy="0" fill="none" stroke="#3d6b35" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4"/>
                  <circle r="42" cx="0" cy="0" fill="url(#hgg)"/>
                  {/* doc + pen icon */}
                  <rect x="-13" y="-16" width="20" height="24" rx="3" fill="white" opacity="0.9"/>
                  <line x1="-9" y1="-10" x2="1"  y2="-10" stroke="#3d6b35" strokeWidth="1.8" strokeLinecap="round"/>
                  <line x1="-9" y1="-5"  x2="1"  y2="-5"  stroke="#3d6b35" strokeWidth="1.8" strokeLinecap="round"/>
                  <line x1="-9" y1="0"   x2="-3" y2="0"   stroke="#3d6b35" strokeWidth="1.8" strokeLinecap="round"/>
                  <g transform="translate(4,4)">
                    <path d="M0,-8 L6,0 L4,2 L-2,-6 Z" fill="white"/>
                    <line x1="4" y1="2" x2="2" y2="6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </g>
                  <text x="0" y="65" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600 }} fill="#3d6b35">Notes Techniques</text>
                  <g className="hero-mod-tooltip">
                    <rect x="-55" y="-90" width="110" height="30" rx="6" fill="#2d5a27"/>
                    <text x="0" y="-70" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 10 }} fill="white">Structurez vos observations</text>
                  </g>
                </g>

                {/* ── Module: Transcription (top-right, 330 90) ── */}
                <g className="hero-mod-circle" transform="translate(330,90)">
                  <circle r="52" cx="0" cy="0" fill="white" filter="url(#hsh)"/>
                  <circle r="52" cx="0" cy="0" fill="none" stroke="#1e7a70" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4"/>
                  <circle r="42" cx="0" cy="0" fill="url(#hgt)"/>
                  {/* mic icon */}
                  <rect x="-7" y="-18" width="14" height="20" rx="7" fill="white" opacity="0.9"/>
                  <path d="M-12,0 Q-12,12 0,12 Q12,12 12,0" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="0" y1="12" x2="0"  y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="-6" y1="18" x2="6" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M14,-6 Q18,0 14,6"   fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
                  <path d="M-14,-6 Q-18,0 -14,6" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
                  <text x="0" y="65" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600 }} fill="#1e7a70">Transcription</text>
                  <g className="hero-mod-tooltip">
                    <rect x="-55" y="-90" width="110" height="30" rx="6" fill="#1e7a70"/>
                    <text x="0" y="-70" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 10 }} fill="white">Audio vers texte en temps réel</text>
                  </g>
                </g>

                {/* ── Module: Codification (bottom-left, 90 290) ── */}
                <g className="hero-mod-circle" transform="translate(90,290)">
                  <circle r="52" cx="0" cy="0" fill="white" filter="url(#hsh)"/>
                  <circle r="52" cx="0" cy="0" fill="none" stroke="#7a6230" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4"/>
                  <circle r="42" cx="0" cy="0" fill="url(#hgo)"/>
                  {/* tag icon */}
                  <path d="M-14,-8 L2,-8 L14,0 L2,8 L-14,8 Z" fill="white" opacity="0.9"/>
                  <circle cx="-8" cy="0" r="3" fill="#9a7c3a"/>
                  <line x1="4"  y1="-4" x2="10" y2="-4" stroke="white" strokeWidth="1.5" opacity="0.5" strokeLinecap="round"/>
                  <line x1="4"  y1="0"  x2="12" y2="0"  stroke="white" strokeWidth="1.5" opacity="0.5" strokeLinecap="round"/>
                  <line x1="4"  y1="4"  x2="10" y2="4"  stroke="white" strokeWidth="1.5" opacity="0.5" strokeLinecap="round"/>
                  <text x="0" y="65" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600 }} fill="#7a6230">Codification</text>
                  <g className="hero-mod-tooltip">
                    <rect x="-55" y="-90" width="110" height="30" rx="6" fill="#7a6230"/>
                    <text x="0" y="-70" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 10 }} fill="white">Catégorisez vos données</text>
                  </g>
                </g>

                {/* ── Module: Rapports (bottom-right, 330 290) ── */}
                <g className="hero-mod-circle" transform="translate(330,290)">
                  <circle r="52" cx="0" cy="0" fill="white" filter="url(#hsh)"/>
                  <circle r="52" cx="0" cy="0" fill="none" stroke="#7a3030" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4"/>
                  <circle r="42" cx="0" cy="0" fill="url(#hgb)"/>
                  {/* bar chart icon */}
                  <rect x="-14" y="4"   width="8" height="12" rx="2" fill="white" opacity="0.9"/>
                  <rect x="-3"  y="-4"  width="8" height="20" rx="2" fill="white"/>
                  <rect x="8"   y="-10" width="8" height="26" rx="2" fill="white" opacity="0.9"/>
                  <line x1="-16" y1="16" x2="18" y2="16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                  <text x="0" y="65" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600 }} fill="#7a3030">Rapports</text>
                  <g className="hero-mod-tooltip">
                    <rect x="-55" y="-90" width="110" height="30" rx="6" fill="#7a3030"/>
                    <text x="0" y="-70" textAnchor="middle" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 10 }} fill="white">Générez vos rapports finaux</text>
                  </g>
                </g>

                {/* ── Center circle ── */}
                <g filter="url(#hsh)">
                  <circle cx="210" cy="190" r="52" fill="white"/>
                  <circle cx="210" cy="190" r="52" fill="none" stroke="#2d5a27" strokeWidth="2"/>
                  <text x="210" y="183" textAnchor="middle" fill="#2d5a27"
                    style={{ fontFamily: '"DM Serif Display", serif', fontSize: 26, fontWeight: 700 }}>Q</text>
                  <text x="210" y="202" textAnchor="middle" fill="#2d5a27"
                    style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '1.5px' }}>QUALYSE</text>
                  {/* Orbit dots */}
                  <circle cx="210" cy="138" r="4" fill="#2d5a27" opacity="0.3"/>
                  <circle cx="210" cy="242" r="4" fill="#2d5a27" opacity="0.3"/>
                  <circle cx="158" cy="190" r="4" fill="#2d5a27" opacity="0.3"/>
                  <circle cx="262" cy="190" r="4" fill="#2d5a27" opacity="0.3"/>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ── BANDEAU DE FAITS PRODUIT ──────────────────────────────────── */}
      <div
        style={{ background: '#2d5a27', borderBottom: '1px solid rgba(0,0,0,0.1)' }}
      >
        <div
          className="hero-sband"
          style={{
            maxWidth: 1140,
            margin: '0 auto',
            padding: '0 60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 90,
            fontFamily: 'var(--font-dm-sans), "DM Sans", sans-serif',
          }}
        >
          {STATS.map((stat, i) => (
            <div
              key={i}
              className="hero-sitem"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flex: 1,
                padding: '20px 0',
                borderRight: i < STATS.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none',
                paddingRight: i < STATS.length - 1 ? 32 : 0,
                marginRight: i < STATS.length - 1 ? 32 : 0,
              }}
            >
              <div style={{
                width: 40, height: 40,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {stat.icon}
              </div>
              <div style={{ color: 'white' }}>
                <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2, fontWeight: 400 }}>
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
